<script>
import { Line, Bar, mixins } from "vue-chartjs";

export default {
  name: 'LineTendencias',
  props: {
    datosGrafico: {
      type: Array,
      default: () => []
    },
    formato: {
      type: String,
      default: '11'
    },
    esUnSoloDia: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      chartInstance: null
    };
  },
  computed: {
    datosFiltrados() {
      if (!this.datosGrafico || this.datosGrafico.length === 0) return [];
      return this.datosGrafico.filter(serie => serie.formato === this.formato);
    },
    
    chartData() {
      if (this.datosFiltrados.length === 0) {
        return {
          labels: [],
          datasets: []
        };
      }
      
      // Agrupar datos por empresa y fecha
      const empresasData = {};
      
      this.datosFiltrados.forEach(serie => {
        if (!empresasData[serie.empresa]) {
          empresasData[serie.empresa] = {};
        }
        
        if (serie.puntos) {
          serie.puntos.forEach(punto => {
            const fecha = punto.fecha;
            if (empresasData[serie.empresa][fecha]) {
              if (punto.precioOferta < empresasData[serie.empresa][fecha].precioOferta) {
                empresasData[serie.empresa][fecha] = punto;
              }
            } else {
              empresasData[serie.empresa][fecha] = punto;
            }
          });
        }
      });
      
      // Obtener fechas únicas y ordenarlas
      const todasFechas = new Set();
      Object.values(empresasData).forEach(fechasEmpresa => {
        Object.keys(fechasEmpresa).forEach(fecha => {
          todasFechas.add(fecha);
        });
      });
      
      const fechasOrdenadas = Array.from(todasFechas).sort();
      
      // Crear datasets limpios
      const datasets = Object.keys(empresasData).map(empresa => {
        const data = fechasOrdenadas.map(fecha => {
          const punto = empresasData[empresa][fecha];
          return punto ? punto.precioOferta : null;
        });
        
        const baseConfig = {
          label: empresa,
          data: data,
          backgroundColor: this.getColorForEmpresa(empresa),
          borderColor: this.getColorForEmpresa(empresa)
        };
        
        if (this.esUnSoloDia) {
          return {
            ...baseConfig,
            borderWidth: 1
          };
        } else {
          return {
            ...baseConfig,
            pointBackgroundColor: this.getColorForEmpresa(empresa),
            pointBorderColor: this.getColorForEmpresa(empresa),
            borderWidth: 3,
            fill: false,
            lineTension: 0.1,
            pointRadius: 6,
            pointHoverRadius: 8
          };
        }
      });
      
      return {
        labels: fechasOrdenadas,
        datasets: datasets
      };
    },
    
    chartOptions() {
      const self = this;
      return {
        scales: {
          yAxes: [{
            ticks: {
              beginAtZero: false,
              callback: function(value) {
                if (parseInt(value) >= 1000) {
                  return '$' + value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
                } else {
                  return '$' + value;
                }
              }
            },
            gridLines: {
              display: true,
              color: 'rgba(0,0,0,0.1)'
            },
            scaleLabel: {
              display: true,
              labelString: 'Precio (CLP)'
            }
          }],
          xAxes: [{
            gridLines: {
              display: true,
              color: 'rgba(0,0,0,0.1)'
            },
            scaleLabel: {
              display: true,
              labelString: self.esUnSoloDia ? 'Empresas' : 'Fecha'
            }
          }]
        },
        tooltips: {
          mode: 'index',
          intersect: false,
          backgroundColor: 'rgba(0,0,0,0.9)',
          titleFontColor: 'white',
          bodyFontColor: 'white',
          borderColor: 'rgba(255,255,255,0.3)',
          borderWidth: 1,
          cornerRadius: 6,
          callbacks: {
            title: function(tooltipItems) {
              if (self.esUnSoloDia) {
                return self.formato + 'kg - ' + tooltipItems[0].xLabel;
              }
              return 'Fecha: ' + tooltipItems[0].xLabel;
            },
            label: function(tooltipItem, data) {
              const dataset = data.datasets[tooltipItem.datasetIndex];
              const empresa = dataset.label;
              const precio = tooltipItem.yLabel;
              
              if (precio === null || precio === undefined) {
                return empresa + ': Sin datos';
              }
              
              const precioFormateado = precio >= 1000 ? 
                '$' + precio.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".") : 
                '$' + precio;
              return empresa + ': ' + precioFormateado;
            }
          }
        },
        legend: {
          display: true,
          position: 'top',
          labels: {
            usePointStyle: !self.esUnSoloDia,
            padding: 20,
            fontSize: 12,
            fontColor: '#333'
          }
        },
        responsive: true,
        maintainAspectRatio: false,
        elements: {
          point: {
            hoverRadius: 10
          },
          line: {
            tension: 0.1
          }
        }
      };
    }
  },
  
  methods: {
    getColorForEmpresa(empresa) {
      const colores = {
        'GASCO GLP S. A.': '#0D47A1',
        'ABASTIBLE S.A.': '#FF9800', 
        'EMPRESAS LIPIGAS S. A.': '#ffc107'
      };
      return colores[empresa] || '#9E9E9E';
    },
    
    renderChart() {
      if (this.chartInstance) {
        this.chartInstance.destroy();
      }
      
      const ctx = this.$refs.canvas.getContext('2d');
      const Chart = window.Chart;
      
      if (!Chart) {
        console.error('Chart.js no está disponible');
        return;
      }
      
      this.chartInstance = new Chart(ctx, {
        type: this.esUnSoloDia ? 'bar' : 'line',
        data: this.chartData,
        options: this.chartOptions
      });
    }
  },
  
  mounted() {
    this.$nextTick(() => {
      this.renderChart();
    });
  },
  
  watch: {
    chartData: {
      handler() {
        this.$nextTick(() => {
          this.renderChart();
        });
      },
      deep: true
    },
    esUnSoloDia() {
      this.$nextTick(() => {
        this.renderChart();
      });
    }
  },
  
  beforeDestroy() {
    if (this.chartInstance) {
      this.chartInstance.destroy();
    }
  }
};
</script>

<template>
  <div style="position: relative; height: 100%;">
    <canvas ref="canvas"></canvas>
  </div>
</templa